// src/pages/TikpagExperience/TikpagExperienceAuditoria.jsx

import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Activity,
  BadgeCheck,
  ChevronLeft,
  ChevronRight,
  CirclePlus,
  Eye,
  FileClock,
  Filter,
  Loader2,
  PencilLine,
  RotateCcw,
  Search,
  Trash2,
  UserRound,
  UsersRound,
  X
} from "lucide-react";

import axios from "../../axiosConfig";
import "./TikpagExperienceAuditoria.css";

const ACOES = {
  INCLUSAO: "Inclusão",
  ALTERACAO: "Alteração",
  EXCLUSAO: "Exclusão",
  APROVACAO: "Aprovação",
  CANCELAMENTO: "Cancelamento",
  REPROCESSAMENTO: "Reprocessamento"
};

const MODULOS = {
  CAMPANHAS: "Campanhas",
  PINS: "PINs",
  CATEGORIAS: "Categorias",
  EXPERIENCIAS: "Experiências",
  ESCOLHAS: "Escolhas",
  ENTREGA_PINS: "Entrega de PINs",
  APURACOES: "Apurações"
};

const CAMPOS = {
  nome: "Nome",
  titulo: "Título",
  descricao: "Descrição",
  regulamento: "Regulamento",
  status: "Status",
  ativo: "Ativo",
  ativa: "Ativa",
  fornecedor: "Fornecedor",
  categoria: "Categoria",
  pins: "PINs vinculados",
  valor_minimo: "Valor mínimo",
  valor_referencia: "Valor de referência",
  validade: "Validade",
  validade_dias: "Validade em dias",
  codigo_voucher: "Código do voucher",
  link_voucher: "Link do voucher",
  instrucoes: "Instruções",
  instrucoes_entrega: "Instruções de entrega",
  observacoes: "Observações",
  pin_status: "Status do PIN",
  tipo_entrega: "Tipo de entrega",
  endereco: "Endereço",
  rastreio: "Rastreio",
  fechamento: "Fechamento",
  competencia: "Competência",
  distribuicao_pins: "Distribuição de PINs",
  conquistas: "Conquistas"
};

const numero = (valor) => Number(valor || 0).toLocaleString("pt-BR");

const dataHora = (valor) => {
  if (!valor) return "-";
  const data = new Date(valor);
  if (Number.isNaN(data.getTime())) return String(valor);
  return data.toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  });
};

const rotuloCampo = (campo) =>
  CAMPOS[campo] ||
  String(campo || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (letra) => letra.toUpperCase());

const valorTexto = (valor, campo = "") => {
  if (valor === null || valor === undefined || valor === "") return "Não informado";
  if (typeof valor === "boolean") return valor ? "Sim" : "Não";

  if (["valor_minimo", "valor_referencia", "volume_total", "volume_apurado"].includes(campo)) {
    return Number(valor).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
  }

  if (Array.isArray(valor)) {
    if (!valor.length) return "Nenhum item";
    return valor
      .map((item) =>
        item && typeof item === "object"
          ? item.nome || item.pin_nome || item.titulo || item.codigo || item.id
          : item
      )
      .join(", ");
  }

  if (valor && typeof valor === "object") {
    if (valor.nome) return valor.nome;
    return JSON.stringify(valor);
  }

  return String(valor);
};

const iconeAcao = (acao) => {
  if (acao === "INCLUSAO") return CirclePlus;
  if (acao === "EXCLUSAO") return Trash2;
  if (acao === "APROVACAO") return BadgeCheck;
  return PencilLine;
};

function ValorDetalhado({ valor, campo }) {
  if (Array.isArray(valor)) {
    return (
      <div className="experienceAuditoria-arrayList">
        {valor.length ? (
          valor.map((item, indice) => (
            <div key={item?.id || item?.conquista_id || indice} className="experienceAuditoria-arrayItem">
              <strong>
                {item && typeof item === "object"
                  ? item.nome || item.pin_nome || item.titulo || item.codigo || `Item ${indice + 1}`
                  : String(item)}
              </strong>
              {item && typeof item === "object" && (
                <small>
                  {item.codigo ? `Código: ${item.codigo}` : ""}
                  {item.ordem ? ` • Ordem: ${item.ordem}` : ""}
                  {item.volume_apurado !== undefined
                    ? ` • Volume: ${valorTexto(item.volume_apurado, "volume_apurado")}`
                    : ""}
                </small>
              )}
            </div>
          ))
        ) : (
          <span className="experienceAuditoria-muted">Nenhum item</span>
        )}
      </div>
    );
  }

  if (valor && typeof valor === "object") {
    return (
      <div className="experienceAuditoria-objectList">
        {Object.entries(valor).map(([chave, conteudo]) => (
          <div key={chave}>
            <span>{rotuloCampo(chave)}</span>
            <strong>{valorTexto(conteudo, chave)}</strong>
          </div>
        ))}
      </div>
    );
  }

  return <span>{valorTexto(valor, campo)}</span>;
}

export default function TikpagExperienceAuditoria() {
  const [dados, setDados] = useState(null);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState("");
  const [pagina, setPagina] = useState(1);
  const [busca, setBusca] = useState("");
  const [buscaAplicada, setBuscaAplicada] = useState("");
  const [modulo, setModulo] = useState("");
  const [acao, setAcao] = useState("");
  const [funcionarioId, setFuncionarioId] = useState("");
  const [dataInicio, setDataInicio] = useState("");
  const [dataFim, setDataFim] = useState("");
  const [selecionado, setSelecionado] = useState(null);

  const carregar = useCallback(async () => {
    setLoading(true);
    setErro("");

    try {
      const resposta = await axios.get("/admin/experience/auditoria", {
        params: {
          pagina,
          limite: 25,
          busca: buscaAplicada || undefined,
          modulo: modulo || undefined,
          acao: acao || undefined,
          usuario_admin_id: funcionarioId || undefined,
          data_inicio: dataInicio || undefined,
          data_fim: dataFim || undefined
        }
      });

      setDados(resposta.data || null);
    } catch (err) {
      console.error("Erro ao carregar auditoria Experience:", err);
      setErro(err.response?.data?.error || "Não foi possível carregar a auditoria.");
    } finally {
      setLoading(false);
    }
  }, [pagina, buscaAplicada, modulo, acao, funcionarioId, dataInicio, dataFim]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  useEffect(() => {
    setPagina(1);
  }, [modulo, acao, funcionarioId, dataInicio, dataFim]);

  useEffect(() => {
    if (!selecionado) return undefined;
    const fechar = (event) => event.key === "Escape" && setSelecionado(null);
    document.addEventListener("keydown", fechar);
    return () => document.removeEventListener("keydown", fechar);
  }, [selecionado]);

  const limpar = () => {
    setBusca("");
    setBuscaAplicada("");
    setModulo("");
    setAcao("");
    setFuncionarioId("");
    setDataInicio("");
    setDataFim("");
    setPagina(1);
  };

  const cards = useMemo(
    () => [
      ["Registros", dados?.cards?.total_registros, FileClock],
      ["Inclusões", dados?.cards?.inclusoes, CirclePlus],
      ["Alterações", dados?.cards?.alteracoes, PencilLine],
      ["Exclusões", dados?.cards?.exclusoes, Trash2],
      ["Aprovações", dados?.cards?.aprovacoes, BadgeCheck],
      ["Funcionários", dados?.cards?.funcionarios_ativos, UsersRound]
    ],
    [dados]
  );

  const auditoria = Array.isArray(dados?.auditoria) ? dados.auditoria : [];
  const modulos = Array.isArray(dados?.filtros?.modulos) ? dados.filtros.modulos : [];
  const funcionarios = Array.isArray(dados?.filtros?.funcionarios)
    ? dados.filtros.funcionarios
    : [];
  const acoes = Array.isArray(dados?.filtros?.acoes) ? dados.filtros.acoes : [];
  const totalPaginas = Number(dados?.paginacao?.total_paginas || 1);
  const alteracoes = selecionado?.alteracoes ? Object.entries(selecionado.alteracoes) : [];

  return (
    <div className="experienceAuditoria-page">
      <header className="experienceAuditoria-header">
        <div>
          <span>Tikpag Experience</span>
          <h1>Auditoria administrativa</h1>
          <p>Acompanhe quem alterou, quando alterou e como os dados estavam antes.</p>
        </div>

        <button type="button" onClick={carregar} disabled={loading}>
          <RotateCcw size={16} className={loading ? "experienceAuditoria-spin" : ""} />
          Atualizar
        </button>
      </header>

      {erro && <div className="experienceAuditoria-alert">{erro}</div>}

      <section className="experienceAuditoria-cards">
        {cards.map(([titulo, valor, Icone]) => (
          <article key={titulo}>
            <div><Icone size={19} /></div>
            <span>{titulo}</span>
            <strong>{numero(valor)}</strong>
          </article>
        ))}
      </section>

      <section className="experienceAuditoria-filters">
        <form
          onSubmit={(event) => {
            event.preventDefault();
            setPagina(1);
            setBuscaAplicada(busca.trim());
          }}
        >
          <Search size={16} />
          <input
            type="search"
            value={busca}
            onChange={(event) => setBusca(event.target.value)}
            placeholder="Buscar funcionário, descrição, módulo ou registro..."
          />
          <button type="submit">Buscar</button>
        </form>

        <label>
          <span>Módulo</span>
          <select value={modulo} onChange={(event) => setModulo(event.target.value)}>
            <option value="">Todos</option>
            {modulos.map((item) => (
              <option key={item} value={item}>{MODULOS[item] || item}</option>
            ))}
          </select>
        </label>

        <label>
          <span>Ação</span>
          <select value={acao} onChange={(event) => setAcao(event.target.value)}>
            <option value="">Todas</option>
            {acoes.map((item) => (
              <option key={item} value={item}>{ACOES[item] || item}</option>
            ))}
          </select>
        </label>

        <label>
          <span>Funcionário</span>
          <select value={funcionarioId} onChange={(event) => setFuncionarioId(event.target.value)}>
            <option value="">Todos</option>
            {funcionarios.map((item) => (
              <option key={item.id} value={item.id}>{item.nome}</option>
            ))}
          </select>
        </label>

        <label>
          <span>De</span>
          <input type="date" value={dataInicio} onChange={(event) => setDataInicio(event.target.value)} />
        </label>

        <label>
          <span>Até</span>
          <input type="date" value={dataFim} onChange={(event) => setDataFim(event.target.value)} />
        </label>

        <button type="button" className="experienceAuditoria-clear" onClick={limpar}>
          <Filter size={15} />
          Limpar
        </button>
      </section>

      <section className="experienceAuditoria-content">
        <header>
          <div>
            <span>Linha do tempo</span>
            <h2>Histórico de atividades</h2>
          </div>
          <strong>{numero(dados?.paginacao?.total_registros)} registros</strong>
        </header>

        {loading ? (
          <div className="experienceAuditoria-loading">
            <Loader2 size={28} className="experienceAuditoria-spin" />
            <span>Carregando auditoria...</span>
          </div>
        ) : auditoria.length ? (
          <div className="experienceAuditoria-timeline">
            {auditoria.map((registro) => {
              const Icone = iconeAcao(registro.acao);
              const mudancas = registro.alteracoes ? Object.entries(registro.alteracoes) : [];

              return (
                <article key={registro.id} className={`action-${String(registro.acao).toLowerCase()}`}>
                  <div className="experienceAuditoria-eventIcon"><Icone size={17} /></div>

                  <div className="experienceAuditoria-eventBody">
                    <div className="experienceAuditoria-eventTop">
                      <div>
                        <div className="experienceAuditoria-tags">
                          <span>{ACOES[registro.acao] || registro.acao}</span>
                          <span>{MODULOS[registro.modulo] || registro.modulo}</span>
                        </div>
                        <h3>{registro.descricao}</h3>
                      </div>

                      <button type="button" onClick={() => setSelecionado(registro)}>
                        <Eye size={15} />
                        Ver detalhes
                      </button>
                    </div>

                    <div className="experienceAuditoria-meta">
                      <span><UserRound size={14} />{registro.funcionario?.nome}</span>
                      <span><Activity size={14} />{dataHora(registro.criado_em)}</span>
                      {registro.campanha?.nome && <span>{registro.campanha.nome}</span>}
                      <span>Registro #{registro.registro_id}</span>
                    </div>

                    {!!mudancas.length && (
                      <div className="experienceAuditoria-preview">
                        {mudancas.slice(0, 3).map(([campo, mudanca]) => (
                          <div key={campo}>
                            <span>{rotuloCampo(campo)}</span>
                            <strong>{valorTexto(mudanca?.anterior, campo)}</strong>
                            <i>→</i>
                            <strong>{valorTexto(mudanca?.novo, campo)}</strong>
                          </div>
                        ))}
                        {mudancas.length > 3 && <small>+{mudancas.length - 3} alterações</small>}
                      </div>
                    )}
                  </div>
                </article>
              );
            })}
          </div>
        ) : (
          <div className="experienceAuditoria-empty">
            <FileClock size={34} />
            <h3>Nenhum registro encontrado</h3>
            <p>Ajuste os filtros para localizar outras atividades.</p>
          </div>
        )}

        <footer className="experienceAuditoria-pagination">
          <button
            type="button"
            disabled={pagina <= 1 || loading}
            onClick={() => setPagina((atual) => Math.max(1, atual - 1))}
          >
            <ChevronLeft size={16} />Anterior
          </button>

          <span>Página <strong>{pagina}</strong> de <strong>{totalPaginas}</strong></span>

          <button
            type="button"
            disabled={pagina >= totalPaginas || loading}
            onClick={() => setPagina((atual) => Math.min(totalPaginas, atual + 1))}
          >
            Próxima<ChevronRight size={16} />
          </button>
        </footer>
      </section>

      {selecionado && (
        <div
          className="experienceAuditoria-modalOverlay"
          onMouseDown={(event) => event.target === event.currentTarget && setSelecionado(null)}
        >
          <div className="experienceAuditoria-modal" role="dialog" aria-modal="true">
            <header>
              <div>
                <span>Registro #{selecionado.id}</span>
                <h2>Detalhes da auditoria</h2>
                <p>{selecionado.descricao}</p>
              </div>
              <button type="button" onClick={() => setSelecionado(null)} aria-label="Fechar">
                <X size={20} />
              </button>
            </header>

            <div className="experienceAuditoria-modalSummary">
              <div><span>Funcionário</span><strong>{selecionado.funcionario?.nome}</strong></div>
              <div><span>Data</span><strong>{dataHora(selecionado.criado_em)}</strong></div>
              <div><span>Módulo</span><strong>{MODULOS[selecionado.modulo] || selecionado.modulo}</strong></div>
              <div><span>Ação</span><strong>{ACOES[selecionado.acao] || selecionado.acao}</strong></div>
            </div>

            <div className="experienceAuditoria-modalContent">
              <div className="experienceAuditoria-sectionTitle">
                <h3>O que mudou</h3>
                <span>{alteracoes.length} campos</span>
              </div>

              {alteracoes.length ? (
                <div className="experienceAuditoria-detailChanges">
                  {alteracoes.map(([campo, mudanca]) => (
                    <article key={campo}>
                      <h4>{rotuloCampo(campo)}</h4>
                      <div>
                        <section className="before">
                          <span>Antes</span>
                          <ValorDetalhado valor={mudanca?.anterior} campo={campo} />
                        </section>
                        <section className="after">
                          <span>Depois</span>
                          <ValorDetalhado valor={mudanca?.novo} campo={campo} />
                        </section>
                      </div>
                    </article>
                  ))}
                </div>
              ) : (
                <div className="experienceAuditoria-noChanges">
                  Esta ação não possui comparação entre valores anteriores e novos.
                </div>
              )}

              <details className="experienceAuditoria-technical">
                <summary>Dados técnicos da requisição</summary>
                <div><span>Método</span><strong>{selecionado.requisicao?.metodo || "-"}</strong></div>
                <div><span>Rota</span><strong>{selecionado.requisicao?.rota || "-"}</strong></div>
                <div><span>IP</span><strong>{selecionado.requisicao?.ip || "-"}</strong></div>
                <div><span>Navegador</span><strong>{selecionado.requisicao?.user_agent || "-"}</strong></div>
              </details>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

